import React, { useState, useEffect } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  AreaChart,
  Area
} from 'recharts'
import {
  Play,
  Settings,
  BarChart3,
  TrendingUp,
  AlertCircle,
  Download,
  RefreshCw
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type {
  MonteCarloParameters,
  MonteCarloResults,
  MonteCarloComponentProps
} from '@/types/risk-assessment'

export function MonteCarloSimulation({
  initial_parameters,
  onRunSimulation,
  results,
  loading = false
}: MonteCarloComponentProps) {
  const [parameters, setParameters] = useState<MonteCarloParameters>(
    initial_parameters || {
      simulation_runs: 10000,
      time_horizon_years: 10,
      variables: [
        {
          name: 'Annual Rent Growth',
          current_value: 3.0,
          volatility: 2.0,
          distribution: 'normal'
        },
        {
          name: 'Property Appreciation',
          current_value: 4.0,
          volatility: 3.0,
          distribution: 'normal'
        },
        {
          name: 'Vacancy Rate',
          current_value: 5.0,
          volatility: 2.5,
          distribution: 'normal'
        },
        {
          name: 'Interest Rate',
          current_value: 6.5,
          volatility: 1.5,
          distribution: 'normal'
        },
        {
          name: 'Operating Expense Growth',
          current_value: 2.5,
          volatility: 1.5,
          distribution: 'normal'
        }
      ]
    }
  )

  const [isRunning, setIsRunning] = useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)

  const handleRunSimulation = async () => {
    setIsRunning(true)
    try {
      await onRunSimulation(parameters)
    } catch (error) {
      console.error('Monte Carlo simulation failed:', error)
      // Error is handled by the parent component
    } finally {
      setIsRunning(false)
    }
  }

  const updateVariable = (index: number, field: string, value: number | string) => {
    setParameters(prev => ({
      ...prev,
      variables: prev.variables.map((variable, i) =>
        i === index ? { ...variable, [field]: value } : variable
      )
    }))
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  // Validate Monte Carlo results before rendering
  const isValidResults = (results: MonteCarloResults | null | undefined): boolean => {
    if (!results) return false

    // Check for new comprehensive API response structure
    const hasNewStructure = !!(
      results.simulation_parameters &&
      results.portfolio_value_projections &&
      results.cash_flow_projections &&
      results.return_analysis &&
      results.risk_metrics
    )

    // Check for old structure (backwards compatibility)
    const hasOldStructure = !!(
      results.outcomes &&
      Array.isArray(results.outcomes) &&
      results.confidence_intervals &&
      Array.isArray(results.confidence_intervals) &&
      results.risk_metrics &&
      typeof results.risk_metrics === 'object'
    )

    return hasNewStructure || hasOldStructure
  }

  // Prepare chart data from results with proper null safety
  // Support both new and old API response structures
  const outcomeData = (() => {
    // Try new structure first - generate percentile data from projections
    if (results?.portfolio_value_projections && results?.cash_flow_projections && results?.return_analysis) {
      const percentiles = [5, 10, 25, 50, 75, 90, 95]
      return percentiles.map(p => ({
        percentile: p,
        portfolio_value: results.portfolio_value_projections?.percentiles[`p${p}` as keyof typeof results.portfolio_value_projections.percentiles] || 0,
        annual_return: results.return_analysis?.annual_returns?.percentiles[`p${p}` as keyof typeof results.return_analysis.annual_returns.percentiles] || 0,
        cash_flow: results.cash_flow_projections?.percentiles[`p${p}` as keyof typeof results.cash_flow_projections.percentiles] || 0
      }))
    }

    // Fall back to old structure
    return results?.outcomes?.map((outcome, index) => ({
      percentile: outcome.percentile || 0,
      portfolio_value: outcome.portfolio_value || 0,
      annual_return: outcome.annual_return || 0,
      cash_flow: outcome.cash_flow || 0
    })) || []
  })()

  const distributionData = (() => {
    // For new structure, create distribution data from percentiles
    if (results?.portfolio_value_projections?.percentiles) {
      const percentiles = results.portfolio_value_projections.percentiles
      return [
        { value: percentiles.p5, probability: 0.05 },
        { value: percentiles.p10, probability: 0.1 },
        { value: percentiles.p25, probability: 0.25 },
        { value: percentiles.p50, probability: 0.5 },
        { value: percentiles.p75, probability: 0.75 },
        { value: percentiles.p90, probability: 0.9 },
        { value: percentiles.p95, probability: 0.95 }
      ]
    }

    // Fall back to old structure
    return results?.probability_distributions?.find(
      dist => dist.metric === 'portfolio_value'
    )?.data_points?.map(point => ({
      value: point.value || 0,
      probability: point.probability || 0
    })) || []
  })()

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">Percentile: {label}th</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.dataKey === 'portfolio_value' && `Portfolio Value: ${formatCurrency(entry.value)}`}
              {entry.dataKey === 'annual_return' && `Annual Return: ${formatPercentage(entry.value)}`}
              {entry.dataKey === 'cash_flow' && `Cash Flow: ${formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className="space-y-8 relative">
      {/* Loading Overlay */}
      {(isRunning || loading) && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600 font-medium">
              {isRunning ? 'Running Monte Carlo Simulation...' : 'Loading...'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {isRunning ? 'This may take a few moments for complex simulations.' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Simulation Parameters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Monte Carlo Simulation Parameters</h3>
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Advanced Settings
            </Button>
            <Button
              onClick={handleRunSimulation}
              disabled={isRunning}
              className="flex items-center gap-2"
            >
              {isRunning ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run Simulation
            </Button>
          </div>
        </div>

        {/* Basic Parameters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Simulation Runs
            </label>
            <input
              type="number"
              value={parameters.simulation_runs}
              onChange={(e) => setParameters(prev => ({
                ...prev,
                simulation_runs: parseInt(e.target.value) || 1000
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1000"
              max="100000"
              step="1000"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 10,000 - 50,000 runs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Horizon (Years)
            </label>
            <input
              type="number"
              value={parameters.time_horizon_years}
              onChange={(e) => setParameters(prev => ({
                ...prev,
                time_horizon_years: parseInt(e.target.value) || 5
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              max="30"
            />
            <p className="text-xs text-gray-500 mt-1">Analysis period in years</p>
          </div>
        </div>

        {/* Variable Parameters */}
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-900">Market Variables</h4>
          {parameters.variables.map((variable, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variable Name
                </label>
                <input
                  type="text"
                  value={variable.name}
                  onChange={(e) => updateVariable(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Value (%)
                </label>
                <input
                  type="number"
                  value={variable.current_value}
                  onChange={(e) => updateVariable(index, 'current_value', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Volatility (%)
                </label>
                <input
                  type="number"
                  value={variable.volatility}
                  onChange={(e) => updateVariable(index, 'volatility', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  step="0.1"
                  min="0"
                />
              </div>

              {showAdvancedSettings && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distribution
                  </label>
                  <select
                    value={variable.distribution}
                    onChange={(e) => updateVariable(index, 'distribution', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="normal">Normal</option>
                    <option value="lognormal">Log-Normal</option>
                    <option value="uniform">Uniform</option>
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Simulation Status */}
      {isRunning && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <LoadingSpinner size="sm" />
            <div>
              <p className="text-blue-900 font-medium">Running Monte Carlo Simulation</p>
              <p className="text-blue-700 text-sm">
                Processing {parameters.simulation_runs.toLocaleString()} scenarios over {parameters.time_horizon_years} years...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invalid Results Message */}
      {results && !isValidResults(results) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-yellow-800 font-medium">Incomplete Simulation Results</p>
              <p className="text-yellow-700 text-sm">
                The simulation completed but some results are missing or malformed. Please try running the simulation again.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {isValidResults(results) && results && (
        <div className="space-y-8">
          {/* Key Statistics */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Simulation Results</h3>
              <Button
                variant="secondary"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Results
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Support new comprehensive API response structure */}
              {results.risk_metrics?.confidence_levels?.map((level) => (
                <div key={level.confidence} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">
                    {level.confidence}% Confidence
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">Portfolio Value Range</div>
                    <div className="text-sm font-medium text-red-600">
                      {formatCurrency(level.portfolio_value_range?.min || 0)}
                    </div>
                    <div className="text-xs text-gray-500">to</div>
                    <div className="text-sm font-medium text-green-600">
                      {formatCurrency(level.portfolio_value_range?.max || 0)}
                    </div>
                  </div>
                </div>
              )) ||
              /* Fall back to old structure */
              (results.confidence_intervals || []).map((interval) => (
                <div key={interval.confidence_level} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">
                    {interval.confidence_level || 0}% Confidence
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">Lower Bound</div>
                    <div className="text-sm font-medium text-red-600">
                      {formatCurrency(interval.lower_bound || 0)}
                    </div>
                    <div className="text-xs text-gray-500">Upper Bound</div>
                    <div className="text-sm font-medium text-green-600">
                      {formatCurrency(interval.upper_bound || 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Metrics */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Metrics</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 border border-red-200 bg-red-50 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <div className="text-sm text-red-700 font-medium">Value at Risk (95%)</div>
                <div className="text-lg font-bold text-red-900">
                  {formatCurrency(results.return_analysis?.var_95 || results.risk_metrics?.var_95 || 0)}
                </div>
              </div>

              <div className="text-center p-4 border border-orange-200 bg-orange-50 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                <div className="text-sm text-orange-700 font-medium">Value at Risk (99%)</div>
                <div className="text-lg font-bold text-orange-900">
                  {formatCurrency(results.return_analysis?.var_99 || results.risk_metrics?.var_99 || 0)}
                </div>
              </div>

              <div className="text-center p-4 border border-purple-200 bg-purple-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <div className="text-sm text-purple-700 font-medium">Probability of Loss</div>
                <div className="text-lg font-bold text-purple-900">
                  {formatPercentage((results.return_analysis?.probability_of_loss || 0) * 100)}
                </div>
              </div>

              <div className="text-center p-4 border border-gray-200 bg-gray-50 rounded-lg">
                <BarChart3 className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                <div className="text-sm text-gray-700 font-medium">Volatility</div>
                <div className="text-lg font-bold text-gray-900">
                  {formatPercentage(results.return_analysis?.volatility || results.risk_metrics?.maximum_drawdown || 0)}
                </div>
              </div>
            </div>
          </div>

          {/* Simulation Summary - New API Response Structure */}
          {results.simulation_parameters && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Simulation Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900">
                    {results.simulation_parameters.runs?.toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-700">Simulation Runs</div>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-900">
                    {results.simulation_parameters.time_horizon} years
                  </div>
                  <div className="text-sm text-green-700">Time Horizon</div>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-900">
                    {formatCurrency(results.simulation_parameters.portfolio_value)}
                  </div>
                  <div className="text-sm text-purple-700">Current Portfolio Value</div>
                </div>

                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-900">
                    {results.simulation_parameters.properties_analyzed}
                  </div>
                  <div className="text-sm text-orange-700">Properties Analyzed</div>
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Value Projections Summary */}
          {results.portfolio_value_projections && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Value Projections</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(results.portfolio_value_projections.mean)}
                  </div>
                  <div className="text-sm text-gray-600">Mean</div>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(results.portfolio_value_projections.median)}
                  </div>
                  <div className="text-sm text-gray-600">Median</div>
                </div>

                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-lg font-bold text-red-900">
                    {formatCurrency(results.portfolio_value_projections.min)}
                  </div>
                  <div className="text-sm text-red-700">Minimum</div>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-900">
                    {formatCurrency(results.portfolio_value_projections.max)}
                  </div>
                  <div className="text-sm text-green-700">Maximum</div>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-900">
                    {formatCurrency(results.portfolio_value_projections.std_deviation)}
                  </div>
                  <div className="text-sm text-blue-700">Std. Deviation</div>
                </div>
              </div>
            </div>
          )}

          {/* Cash Flow Projections Summary */}
          {results.cash_flow_projections && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Cash Flow Projections</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(results.cash_flow_projections.mean)}
                  </div>
                  <div className="text-sm text-gray-600">Mean</div>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">
                    {formatCurrency(results.cash_flow_projections.median)}
                  </div>
                  <div className="text-sm text-gray-600">Median</div>
                </div>

                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-lg font-bold text-red-900">
                    {formatCurrency(results.cash_flow_projections.min)}
                  </div>
                  <div className="text-sm text-red-700">Minimum</div>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-900">
                    {formatCurrency(results.cash_flow_projections.max)}
                  </div>
                  <div className="text-sm text-green-700">Maximum</div>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-900">
                    {formatCurrency(results.cash_flow_projections.std_deviation)}
                  </div>
                  <div className="text-sm text-blue-700">Std. Deviation</div>
                </div>
              </div>
            </div>
          )}

          {/* Variable Ranges - New API Response Structure */}
          {results.variable_ranges && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Variable Analysis Ranges</h4>
              <div className="space-y-4">
                {results.variable_ranges.map((variable, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border border-gray-200 rounded-lg">
                    <div className="font-medium text-gray-900">
                      {variable.variable}
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-blue-900">
                        {variable.current_value.toFixed(2)}%
                      </div>
                      <div className="text-xs text-blue-700">Current Value</div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">
                        ±{variable.volatility.toFixed(2)}%
                      </div>
                      <div className="text-xs text-gray-700">Volatility</div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-red-900">
                        {variable.min_range.toFixed(2)}%
                      </div>
                      <div className="text-xs text-red-700">Min Range</div>
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-green-900">
                        {variable.max_range.toFixed(2)}%
                      </div>
                      <div className="text-xs text-green-700">Max Range</div>
                    </div>

                    <div className="text-center">
                      <div className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 capitalize">
                        {variable.distribution}
                      </div>
                      <div className="text-xs text-gray-700 mt-1">Distribution</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outcome Distribution Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Value Outcomes</h4>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <LineChart data={outcomeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="percentile"
                    label={{ value: 'Percentile', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis
                    label={{ value: 'Portfolio Value ($)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="portfolio_value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Probability Distribution */}
          {distributionData.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Probability Distribution</h4>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <AreaChart data={distributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="value"
                      label={{ value: 'Portfolio Value ($)', position: 'insideBottom', offset: -5 }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <YAxis
                      label={{ value: 'Probability', angle: -90, position: 'insideLeft' }}
                      tickFormatter={(value) => formatPercentage(value * 100)}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatPercentage(value * 100),
                        'Probability'
                      ]}
                      labelFormatter={(value) => `Value: ${formatCurrency(value as number)}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="probability"
                      stroke="#8b5cf6"
                      fill="#8b5cf6"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Annual Returns Scatter Plot */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk vs Return Analysis</h4>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <ScatterChart data={outcomeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="annual_return"
                    label={{ value: 'Annual Return (%)', position: 'insideBottom', offset: -5 }}
                    tickFormatter={(value) => formatPercentage(value)}
                  />
                  <YAxis
                    dataKey="portfolio_value"
                    label={{ value: 'Portfolio Value ($)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Scatter
                    dataKey="portfolio_value"
                    fill="#10b981"
                    fillOpacity={0.6}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}